import React, { useState, useCallback } from 'react';
import { View, Platform } from 'react-native';
import { Text, Card } from '@constants/Themed';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import apis from '@api/api';
import { useDispatch } from 'react-redux';
import { SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import { useTranslation } from 'react-i18next';
import { YStack, H5, SizableText, Button } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialCommunityIcons } from '@expo/vector-icons';

interface DatabaseConnectionFormProps {
  onSuccess?: (connection: any) => void;
  onCancel?: () => void;
  initialData?: any;
  scope?: 'user' | 'company';
  companyId?: string;
}

const DatabaseConnectionForm: React.FC<DatabaseConnectionFormProps> = ({
  onSuccess,
  onCancel,
  initialData,
  scope = 'user',
  companyId
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = themeSelector();
  const [testing, setTesting] = useState(false);

  const databaseTypes = [
    { label: 'PostgreSQL', value: 'postgresql' },
    { label: 'MySQL', value: 'mysql' },
    { label: 'MongoDB', value: 'mongodb' },
    { label: 'SQLite', value: 'sqlite' }
  ];

  const formSchema = [
    {
      title: 'name',
      type: 'string',
      validation: { required: true },
      content: { label: 'Connection Name', placeholder: 'My Database Connection' }
    },
    {
      title: 'type',
      type: 'dropDown',
      validation: { required: true },
      content: {
        label: 'Database Type',
        enum: databaseTypes.map(dt => ({ label: dt.label, value: dt.value }))
      }
    },
    {
      title: 'host',
      type: 'string',
      validation: { required: true },
      content: { label: 'Host', placeholder: 'localhost' }
    },
    {
      title: 'port',
      type: 'number',
      validation: { required: true },
      content: { label: 'Port', placeholder: '5432' }
    },
    {
      title: 'database',
      type: 'string',
      validation: { required: true },
      content: { label: 'Database Name', placeholder: 'mydatabase' }
    },
    {
      title: 'username',
      type: 'string',
      validation: { required: true },
      content: { label: 'Username', placeholder: 'dbuser' }
    },
    {
      title: 'password',
      type: 'password',
      validation: { required: true },
      content: { label: 'Password', placeholder: 'Enter password' }
    },
    {
      title: 'sslEnabled',
      type: 'boolean',
      content: { label: 'Enable SSL' }
    },
    ...(scope === 'company' ? [{
      title: 'companyId',
      type: 'string',
      content: { label: 'Company ID' }
    }] : [])
  ];

  const handleSubmit = useCallback(async (values: any) => {
    try {
      dispatch({ type: SHOW_LOADING_MODAL });

      const connectionData = {
        name: values.name,
        type: values.type,
        host: values.host,
        port: parseInt(values.port) || (values.type === 'postgresql' ? 5432 : values.type === 'mysql' ? 3306 : values.type === 'mongodb' ? 27017 : null),
        database: values.database,
        username: values.username,
        password: values.password,
        sslEnabled: values.sslEnabled || false,
        scope: scope,
        ...(scope === 'company' && companyId ? { companyId } : {}),
        tags: []
      };

      const connection = await apis.createDatabaseConnection(connectionData);

      dispatch({ type: HIDE_LOADING_MODAL });

      if (onSuccess) {
        onSuccess(connection);
      }
    } catch (error) {
      dispatch({ type: HIDE_LOADING_MODAL });
      console.error('Error creating database connection:', error);
    }
  }, [dispatch, scope, companyId, onSuccess]);

  return (
    <YStack
      flex={1}
      backgroundColor={theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)'}
      borderRadius={16}
      padding="$4"
      gap="$3"
    >
      <H5
        color={theme ? '#1e293b' : '#f1f5f9'}
        fontWeight="600"
        fontSize={Platform.OS === 'web' ? "$6" : "$5"}
        marginBottom="$2"
      >
        {initialData ? 'Edit Database Connection' : 'Connect Database'}
      </H5>

      <SizableText
        color={theme ? '#64748b' : '#94a3b8'}
        size="$3"
        marginBottom="$2"
      >
        Enter your database connection details to connect to an external database.
      </SizableText>

      <GenerateSchemaForm
        schema={formSchema}
        submitFunction={handleSubmit}
        buttonLabel={initialData ? 'Update Connection' : 'Create Connection'}
        initialData={initialData}
        scrollEnabled={true}
        customStyling={{
          minWidth: "100%",
          flex: 1,
          maxWidth: "100%",
        }}
        onCancel={onCancel}
        cancelButtonLabel="Cancel"
      />
    </YStack>
  );
};

export default DatabaseConnectionForm;

